import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { authService } from "../services/auth.service";
import { validationErrors } from "../services/http";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await login(email, password, remember);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const fields = validationErrors(err);
      setError(fields.email?.[0] ?? (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleForgot() {
    if (!email) {
      setError("Informe seu e-mail para receber as instruções.");
      return;
    }
    setError(null);
    const response = await authService.forgotPassword(email);
    setNotice(response.message);
  }

  return (
    <div className="mx-auto w-full max-w-sm">
      <h1 className="text-lg font-semibold tracking-wide">Entrar no Okton Builder</h1>
      <p className="mt-1 text-sm text-ink-muted">Acesso restrito a usuários cadastrados.</p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-ink-muted">E-mail</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-line/60 bg-surface-raised/60 px-3 py-2 text-sm outline-none focus:border-brand"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-ink-muted">Senha</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-line/60 bg-surface-raised/60 px-3 py-2 text-sm outline-none focus:border-brand"
          />
        </label>

        <label className="flex items-center gap-2 text-xs text-ink-muted">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          Manter conectado
        </label>

        {error && <p className="text-sm text-red-400">{error}</p>}
        {notice && <p className="text-sm text-emerald-400">{notice}</p>}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-brand px-3 py-2 text-sm font-semibold text-brand-ink disabled:opacity-60"
        >
          {busy ? "Entrando…" : "Entrar"}
        </button>

        <button
          type="button"
          onClick={handleForgot}
          className="w-full text-center font-mono text-xs text-ink-muted underline-offset-4 hover:underline"
        >
          Esqueci minha senha
        </button>
      </form>
    </div>
  );
}
